import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnInit,
	ViewChild,
} from '@angular/core';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrService } from 'ngx-toastr';
import { AsyncSubject, Observable, of } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil } from 'rxjs/operators';
import spacetime from 'spacetime';
import { getEnvironment } from 'src/lib/environment/environment';
import { UserUpdateRequestArgument } from 'src/lib/services/api/users/update-request/users-update-request.argument';
import {
	ProofRequirementModel,
	UserUpdateRequestOptions,
} from 'src/lib/services/api/users/update-request/users-update-request.model';
import { UsersUpdateRequestsService } from 'src/lib/services/api/users/update-request/users-update-requests.service';
import {
	UserDataProfileModel,
	UserDataStudentModel,
} from 'src/lib/services/api/users/user-data.model';
import { FormLabelComponent } from 'src/lib/templates/global/form-label/form-label.component';
import { GroupValidationModule } from 'src/lib/templates/layout/group-validation/group-validation.module';
import { FormControlWrapper } from 'src/lib/types/forms.def';
import { StringEnumOption } from 'src/lib/utilities/api/patterns/dynamic-fields/string-enum-option.model';
import {
	getChangedValues,
	hasKey,
	hasSomeKey,
	hasValue,
	isArray,
	isEquivalentObject,
	isEquivalentString,
	isNullOrEmptyString,
} from 'src/lib/utilities/compare';
import { commonValidators } from 'src/lib/utilities/forms';
import { UploadProofModalService } from 'src/lib/views/modals/proof-modal/upload-proof-modal.service';
import { SingleDatePickerComponent } from '../../../../../templates/controls/single-date-picker/single-date-picker.component';
import { InputTrimDirective } from '../../../../../templates/global/email-trim/input-trim.directive';
import { IconTooltipComponent } from '../../../../../templates/global/icon-tooltip/icon-tooltip.component';
import { ProfileListenerService } from '../../profile-listener.service';

export interface ProfileInfoFormGroup {
	profile_first_name: string;
	profile_middle_name: string;
	profile_last_name: string;
	profile_maiden_name: string;
	profile_suffix: string;
	profile_preferred_name: string;
	profile_pronunciation: string;
	dob: Date;
	profile_student_gender: string;
	profile_field_race: string;
	profile_field_ethnicity: string;
	about_me: string;
	city: string;
	state: string;
	intro_video: string;
}

@Component({
	selector: 'ae-profile-info-edit',
	templateUrl: './profile-info-edit.component.html',
	styleUrls: ['./profile-info-edit.component.scss'],
	imports: [
		FormLabelComponent,
		FormsModule,
		GroupValidationModule,
		IconTooltipComponent,
		InputTrimDirective,
		NgSelectModule,
		ReactiveFormsModule,
		SingleDatePickerComponent,
	],
})
export class ProfileInfoEditComponent implements OnInit {
	private _unsubscribe$ = new AsyncSubject<null>();

	@Input() public profileData: UserDataProfileModel;
	@Input() public studentData: UserDataStudentModel;
	@Input() public loading: boolean;
	@Input() public canBypassUpdateProofRequirement: boolean;
	@Input() public enableEdit: () => void;
	@Input() public accountOptions: UserUpdateRequestOptions;
	@Input() public currentUserId: number;
	@Input() public resetProfileInfoForm: (forceRefresh: boolean) => void;

	@ViewChild('rootElement', { static: true })
	rootElement: ElementRef<HTMLElement>;

	public isStudentApp = getEnvironment().isApp('student');

	public savingInfo: boolean = false;

	public profileInfoFormGroup: FormGroup<
		FormControlWrapper<ProfileInfoFormGroup>
	>;

	public dropdownGender: StringEnumOption[];
	public dropdownRace: StringEnumOption[];
	public dropdownEthnicity: StringEnumOption[];
	public dropdownState: StringEnumOption[];

	public currentDate = new Date();

	public birthdateAllowed: boolean = false;
	public hasChanges: boolean = false;

	public hasKey = hasKey;
	public hasSomeKey = hasSomeKey;

	protected proofMap = new Map<string, ProofRequirementModel>();
	private readonly profileApiFieldByFormKey: Record<string, string> = {
		about_me: 'profile_about',
		city: 'profile_city',
		state: 'profile_state',
		intro_video: 'profile_intro_video',
	};
	private originalForm;

	private youtubeRegex =
		/(https:\/\/)(www\.)?youtu(((?:.*\/v\/|.*v=|\.be\/))|(be\.com\/shorts\/))([A-Za-z0-9_-]{11})/;

	constructor(
		private fb: FormBuilder,
		private userRequestService: UsersUpdateRequestsService,
		private toastr: ToastrService,
		private cdr: ChangeDetectorRef,
		private profileListenerService: ProfileListenerService,
		private uploadProofModalService: UploadProofModalService,
	) {}

	ngOnInit() {
		this.profileInfoFormGroup = this.fb.group<
			FormControlWrapper<ProfileInfoFormGroup>
		>({
			// Profile
			profile_first_name: new FormControl(
				this.profileData?.profile_first_name,
				[Validators.required, commonValidators.requiredNotEmpty],
			),
			profile_last_name: new FormControl(this.profileData?.profile_last_name, [
				Validators.required,
				commonValidators.requiredNotEmpty,
			]),
			profile_middle_name: new FormControl(
				this.profileData?.profile_middle_name,
			),
			profile_maiden_name: new FormControl(
				this.profileData?.profile_maiden_name,
			),
			profile_suffix: new FormControl(this.profileData?.profile_suffix),
			profile_preferred_name: new FormControl(
				this.profileData?.profile_preferred_name,
				[
					(ctrl) => {
						if (
							!isNullOrEmptyString(ctrl.value) &&
							isEquivalentString(
								ctrl.value,
								this.profileInfoFormGroup?.get('profile_first_name')?.value,
							)
						) {
							return {
								same_as_first_name: `Preferred name cannot be the same as first name`,
							};
						}

						return null;
					},
				],
			),
			dob: new FormControl(this.studentData?.dob, [
				(ctrl) => {
					if (
						hasValue(ctrl.value) &&
						hasValue(this.accountOptions.student_data?.dob?.minimum_age)
					) {
						const minimumDate = spacetime()
							.subtract(
								this.accountOptions.student_data.dob.minimum_age,
								'year',
							)
							.startOf('day');
						if (minimumDate.isBefore(ctrl.value)) {
							return {
								minimum_age: `Must be at least ${this.accountOptions.student_data.dob.minimum_age} years old`,
							};
						}
					}

					return null;
				},
			]),
			profile_field_race: new FormControl(this.profileData?.profile_field_race),
			profile_field_ethnicity: new FormControl(
				this.profileData?.profile_field_ethnicity,
			),
			profile_student_gender: new FormControl(
				this.profileData?.profile_student_gender,
			),
			profile_pronunciation: new FormControl(
				this.profileData?.profile_pronunciation,
			),
			// Social
			about_me: new FormControl(this.profileData?.profile_about),
			state: new FormControl(this.profileData?.profile_state),
			city: new FormControl(this.profileData?.profile_city),
			intro_video: new FormControl(
				this.profileData?.profile_intro_video
					? `https://www.youtube.com/watch?v=${this.profileData?.profile_intro_video}`
					: '',
				[
					(ctrl) => {
						if (hasValue(ctrl.value)) {
							if (!this.youtubeRegex.test(ctrl.value.trim())) {
								return {
									valid_link: `Must be a valid youtube link`,
								};
							}
						}

						return null;
					},
				],
			),
		});

		this.proofMap = this.getProofRequirements();

		this.cdr.detectChanges();

		// Permissions
		if (hasKey(this.studentData, 'dob')) {
			this.birthdateAllowed = true;
		} else {
			this.profileInfoFormGroup.controls.dob.disable();
		}

		if (!hasKey(this.profileData, 'profile_about')) {
			this.profileInfoFormGroup.controls.about_me.disable();
		}

		if (!hasKey(this.profileData, 'profile_state')) {
			this.profileInfoFormGroup.controls.state.disable();
		}

		if (!hasKey(this.profileData, 'profile_city')) {
			this.profileInfoFormGroup.controls.city.disable();
		}

		if (!hasKey(this.profileData, 'profile_intro_video')) {
			this.profileInfoFormGroup.controls.intro_video.disable();
		}

		this.dropdownGender =
			this.accountOptions?.profile?.profile_student_gender?.options;
		this.dropdownRace =
			this.accountOptions?.profile?.profile_field_race?.options;
		this.dropdownEthnicity =
			this.accountOptions?.profile?.profile_field_ethnicity?.options;

		this.dropdownState =
			this.accountOptions?.contact_field?.physical_address?.subfields?.address?.subfields?.state?.options;

		this.originalForm = this.profileInfoFormGroup.value;

		this.profileInfoFormGroup.valueChanges
			.pipe(debounceTime(1000))
			.subscribe(() => {
				const controlGroup = this.profileInfoFormGroup.controls;
				this.hasChanges = Object.keys(controlGroup).some((key) => {
					if (key === 'intro_video') {
						//perform regex check
						const formURLMatch =
							this.profileInfoFormGroup.controls.intro_video.value.match(
								this.youtubeRegex,
							);
						const introURLMatch = this.profileData.profile_intro_video
							? `https://www.youtube.com/watch?v=${this.profileData.profile_intro_video}`.match(
									this.youtubeRegex,
								)
							: [];
						if (formURLMatch) {
							const len = formURLMatch.length;
							const introVideoLen = introURLMatch.length;
							const intro_video = formURLMatch[len - 1];
							const original_intro_video = introVideoLen
								? introURLMatch[introVideoLen - 1]
								: null;
							return original_intro_video !== intro_video;
						} else {
							return (
								isNullOrEmptyString(
									this.profileInfoFormGroup.controls.intro_video.value,
								) && !isNullOrEmptyString(this.profileData.profile_intro_video)
							);
						}
					} else {
						if (
							this.profileInfoFormGroup.value[key]?.trim != null &&
							this.originalForm[key]?.trim != null
						) {
							return (
								this.profileInfoFormGroup.value[key]?.trim() !==
								this.originalForm[key]?.trim()
							);
						} else if (this.profileInfoFormGroup.value[key] instanceof Date) {
							return !spacetime(this.profileInfoFormGroup.value[key])
								.startOf('day')
								.isEqual(
									spacetime(this.originalForm[key])
										.startOf('day')
										.toNativeDate(),
								);
						} else {
							return (
								this.profileInfoFormGroup.value[key] !== this.originalForm[key]
							);
						}
					}
				});
			});
	}

	public save = () => {
		this.savingInfo = true;
		const controlGroup = this.profileInfoFormGroup.value;
		let intro_video = null;

		let obs$: Observable<
			{
				ids: string[];
				file: File;
			}[]
		> = of([]);

		const changedValues = getChangedValues(this.originalForm, controlGroup);
		const changedFields = changedValues ?? [];
		const map = new Map<string, ProofRequirementModel>();

		if (changedValues && this.proofMap.size > 0) {
			changedValues.forEach((v) => {
				const p = this.proofMap.get(v.key);
				if (
					p?.required &&
					!this.canBypassUpdateProofRequirement &&
					!isEquivalentObject(v.original as string, v.updated as string)
				) {
					map.set(v.key, this.proofMap.get(v.key));
				}
			});
			if (map.size > 0) {
				obs$ = this.proofModal$(map);
			}
		}

		obs$
			.pipe(
				takeUntil(this._unsubscribe$),
				filter((x) => x != null),
				switchMap((proofFiles) => {
					if (controlGroup.intro_video) {
						const URLMatch = controlGroup.intro_video.match(this.youtubeRegex);
						const len = URLMatch.length;
						intro_video = URLMatch[len - 1];
					}

					const profileContactArgs: UserUpdateRequestArgument = {
						profile: {
							profile_first_name: controlGroup.profile_first_name,
							profile_middle_name: controlGroup.profile_middle_name,
							profile_suffix: controlGroup.profile_suffix,
							profile_last_name: controlGroup.profile_last_name,
							profile_maiden_name: controlGroup.profile_maiden_name,
							profile_preferred_name: controlGroup.profile_preferred_name,
							profile_about: controlGroup.about_me,
							profile_city: controlGroup.city,
							profile_state: controlGroup.state,
							profile_field_race: controlGroup.profile_field_race,
							profile_field_ethnicity: controlGroup.profile_field_ethnicity,
							profile_student_gender: controlGroup.profile_student_gender,
							profile_pronunciation: controlGroup.profile_pronunciation,
							profile_intro_video: intro_video,
						},
					};

					if (this.isStudentApp) {
						delete profileContactArgs.profile.profile_pronunciation;
					}

					if (
						isNullOrEmptyString(
							profileContactArgs.profile.profile_preferred_name,
						) ||
						isEquivalentString(
							profileContactArgs.profile.profile_preferred_name,
							profileContactArgs.profile.profile_first_name,
						)
					) {
						delete profileContactArgs.profile.profile_preferred_name;
					}

					Object.keys(profileContactArgs.profile).forEach((key) => {
						const formKey = this.getProfileFormFieldKey(key);
						if (
							!this.hasKey(
								this.profileData,
								key as keyof UserDataProfileModel,
							) ||
							!changedFields.find((x) => x.key === formKey)
						) {
							delete profileContactArgs.profile[key];
						}
					});

					if (Object.keys(profileContactArgs.profile).length === 0) {
						delete profileContactArgs.profile;
					}

					if (
						this.birthdateAllowed &&
						this.profileInfoFormGroup.controls.dob.enabled &&
						this.studentData?.dob !== controlGroup.dob
					) {
						profileContactArgs.student_data = {
							dob: controlGroup.dob,
						};
					}

					this.profileInfoFormGroup.disable();

					return this.userRequestService.createUpdateRequest(
						this.currentUserId,
						profileContactArgs,
						proofFiles,
						this.canBypassUpdateProofRequirement,
					);
				}),
			)
			.subscribe({
				next: (response) => {
					this.savingInfo = false;
					this.profileInfoFormGroup.enable();
					if (response.success) {
						if (
							response.update_request?.updates &&
							!isArray(response.update_request?.updates)
						) {
							this.toastr.success(
								'Profile info update has been requested. Your changes will not be shown until they are approved.',
							);
						} else {
							this.toastr.success('Profile info has been updated.');
						}
						this.profileListenerService.sendSignal('user.name');

						this.resetProfileInfoForm(true);
					} else {
						this.toastr.error(
							`There was an error when updating profile info ${(
								response as unknown as { error_messages: string[] }
							)?.error_messages.join('\n')}`,
						);
					}
				},
				error: () => {
					this.savingInfo = false;
					this.profileInfoFormGroup.enable();

					this.toastr.error(`There was an error when updating profile info`);
				},
				complete: () => {
					this.savingInfo = false;
				},
			});
	};

	protected getProofRequirements = (): Map<string, ProofRequirementModel> => {
		const controlGroup = this.profileInfoFormGroup.value;

		const proofMap = new Map<string, ProofRequirementModel>();
		if (this.accountOptions) {
			Object.keys(controlGroup).forEach((key) => {
				let accountOption = 'profile';
				let accountField = this.getProfileApiFieldKey(key);
				if (key === 'dob') {
					accountOption = 'student_data';
					accountField = key;
				}
				if (
					this.accountOptions[accountOption][accountField]?.proof_requirement
						?.required
				) {
					const val =
						this.accountOptions[accountOption][accountField]?.proof_requirement;
					if (val) {
						val['title'] =
							this.accountOptions[accountOption][accountField].title;
					}

					proofMap.set(key, val);
				}
			});
		}

		return proofMap;
	};

	private getProfileApiFieldKey = (formKey: string): string => {
		return this.profileApiFieldByFormKey[formKey] ?? formKey;
	};

	private getProfileFormFieldKey = (apiField: string): string => {
		return (
			Object.entries(this.profileApiFieldByFormKey).find(
				([, mappedApiField]) => mappedApiField === apiField,
			)?.[0] ?? apiField
		);
	};

	protected proofModal$ = (proofs: Map<string, ProofRequirementModel>) => {
		return this.uploadProofModalService.openModal$(proofs);
	};
}
